<template>
  <div>
    <h1>
      <v-icon size="16px" color="black">mdi-music-note</v-icon>
      {{ $t("chapter.audio.audioSettings") }}
    </h1>
    <v-expansion-panels :value="0">
      <v-expansion-panel>
        <v-expansion-panel-header>{{
          $t("chapter.audio.audioContents")
        }}</v-expansion-panel-header>
        <v-expansion-panel-content v-if="editor">
          <div>
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card elevation="0" color="transparent mb-3">
                  <v-card-text>
                    <div v-if="audioUrl" :key="audioUrl">
                      <v-sheet class="rounded-xl" color="transparent">
                        <v-row
                          class="pa-1 px-2 rounded-t-lg"
                          style="background-color: rgba(0, 0, 0, 0.08)"
                          ><span :title="truncateUrl(audioUrl)"
                            >{{ $t("mediaObject.fileName") }}:
                            {{ truncateUrl(audioUrl, 13) }}</span
                          ></v-row
                        >
                        <v-row
                          class="pa-1 px-2"
                          style="background-color: rgba(0, 0, 0, 0.15)"
                          >{{ $t("mediaObject.fileSize") }}:&nbsp;
                          <v-progress-circular
                            v-if="infoLoading"
                            indeterminate
                            size="14"
                          ></v-progress-circular>
                          <span v-else>
                            {{
                              mediaObject && mediaObject.fileSize
                                ? " " +
                                  bytesToMegabytes(mediaObject.fileSize) +
                                  " MB"
                                : $t("unknown")
                            }}
                          </span>
                        </v-row>
                        <v-row
                          class="pa-1 px-2 rounded-b-lg"
                          style="background-color: rgba(0, 0, 0, 0.08)"
                          >{{ $t("mediaObject.duration") }}:&nbsp;

                          <v-progress-circular
                            v-if="infoLoading"
                            indeterminate
                            size="14"
                          ></v-progress-circular>
                          <span v-else>
                            {{
                              mediaObject && mediaObject.playtimeSeconds
                                ? " " +
                                  readableTime(mediaObject.playtimeSeconds)
                                : $t("unknown")
                            }}
                          </span>
                        </v-row>
                      </v-sheet>
                    </div>
                    <div
                      :class="
                        audioUrl
                          ? null
                          : 'py-15 mb-4 image-upload-container upload-container rounded-lg'
                      "
                    >
                      <v-fade-transition>
                        <v-overlay
                          v-show="
                            (hover && !loading && !audioUrl) ||
                            !audioUrl ||
                            audioUrl == ''
                          "
                          absolute
                          :color="audioUrl ? 'black' : 'transparent'"
                        >
                          <MediaObjectUpload
                            v-model="uploadUrl"
                            :addText="$t('chapter.audio.chooseAudio')"
                            :changeText="$t('chapter.audio.chooseAudio')"
                            @error="setErrorMessage"
                            @fileInfo="handleFileInfo"
                            @loading="loading = $event"
                            :errorTooLargeMessage="
                              $t('chapter.audio.audioTooLarge', {
                                maxAudioSize: MAX_AUDIO_SIZE_MB,
                              })
                            "
                            :maxSizeBytes="MAX_AUDIO_SIZE_BYTES"
                            :formats="AUDIO_FORMATS"
                          />
                        </v-overlay>
                      </v-fade-transition>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </div>
          <UploadErrorMessage :errorMessage="errorMessage" :loading="loading" />
          <v-divider 
            v-if="mediaObjectId"
          />
          <div class="text-left my-3" v-if="loading">
            {{ $t("forms.uploading") }}
            <v-progress-linear
              indeterminate
              color="primaryAction darken-2"
            ></v-progress-linear>
          </div>
          <DeleteMediaObject
            @deleted="handleDeleteMediaObject"
            v-if="mediaObjectId"
            :id="mediaObjectId"
            type="audio"
            :key="mediaObjectId"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
<script>
import UploadErrorMessage from "./UploadErrorMessage.vue"
import MediaObjectUpload from "@/components/MediaObjectUpload";
import { AUDIO_FORMATS } from "@/const";
const EXTENSION_NAME = "audio";
const MAX_AUDIO_SIZE_MB = 50;
const MAX_AUDIO_SIZE_BYTES = MAX_AUDIO_SIZE_MB * 1024 * 1024;
import gql from "graphql-tag";
import {
  bytesToMegabytes,
  secondsToMinutesSecondsString,
} from "@/helpers/conversions";
import { getTruncatedFilenameWithExtensionFromMediaUrl } from "@/helpers/truncateString";
import DeleteMediaObject from "./DeleteMediaObject";
export default {
  props: {
    editor: Object,
  },
  components: {
    MediaObjectUpload,
    DeleteMediaObject,
    UploadErrorMessage,
  },
  apollo: {
    mediaObject: {
      query: gql`
        query MediaObject($id: ID!) {
          mediaObject(id: $id) {
            fileSize
            fileFormat
            playtimeSeconds
          }
        }
      `,
      variables() {
        return {
          id: this.mediaObjectId,
        };
      },
      skip() {
        return !this.mediaObjectId;
      },
    },
  },
  data() {
    return {
      loading: false,
      uploadUrl: null,
      errorMessage: "",
      errorMessageContainer: true,
    };
  },
  methods: {
    setErrorMessage(message) {
      this.errorMessage = message;
    },
    handleFileInfo(fileInfo) {
      this.editor.chain().focus().setAudioMediaObjectId(fileInfo.id).run();
    },
    handleDeleteMediaObject() {
      this.editor.chain().focus().clearAudioMediaObject().run();
    },
    readableTime(time) {
      return secondsToMinutesSecondsString(time);
    },
    bytesToMegabytes(bytes) {
      return bytesToMegabytes(bytes);
    },
    truncateUrl(url, length = null) {
      return getTruncatedFilenameWithExtensionFromMediaUrl(url, length);
    },
  },
  computed: {
    infoLoading() {
      return this.$apollo.queries.mediaObject.loading;
    },
    MAX_AUDIO_SIZE_BYTES() {
      return MAX_AUDIO_SIZE_BYTES;
    },
    MAX_AUDIO_SIZE_MB() {
      return MAX_AUDIO_SIZE_MB;
    },
    AUDIO_FORMATS() {
      return AUDIO_FORMATS;
    },
    isActive() {
      return this.editor.isActive(EXTENSION_NAME);
    },
    audioUrl() {
      return this.editor.getAttributes(EXTENSION_NAME).src;
    },
    mediaObjectId() {
      return this.editor.getAttributes(EXTENSION_NAME).id;
    },
  },
  watch: {
    uploadUrl(newValue) {
      this.editor.chain().focus().setAudioSrc(newValue).run();
    },
    async audioUrl(newValue) {
      this.uploadUrl = newValue;
    },
    loading(newValue) {
      if (newValue) {
        this.errorMessage = null;
        this.errorMessageContainer = true
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.audio-preview {
  width: 100%;
  min-height: 100px;
}

</style>